/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate } from 'react-router';
import {
    Button,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Persona,
    Spinner,
    Text,
    Toolbar,
    ToolbarButton,
    ToolbarDivider,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import {
    AppRecent20Filled,
    AppRecent20Regular,
    Board20Filled,
    Board20Regular,
    Briefcase20Filled,
    Briefcase20Regular,
    bundleIcon,
    DataTrending20Filled,
    DataTrending20Regular,
    MoreVerticalFilled,
    NavigationFilled,
    People20Filled,
    People20Regular,
    PuzzleCubePiece20Filled,
    PuzzleCubePiece20Regular,
    Settings20Filled,
    Settings20Regular,
    SignOut24Regular,
    WeatherMoonRegular,
    WeatherSunnyRegular,
} from '@fluentui/react-icons';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { Column, Paths, Row } from '../../common';
import { TimeTrackingEntry } from '../../../data/types';
import { useThemeService } from '../../../data/services/useThemeService';
import { useActiveTimeEntryService } from '../../../data/services/useActiveTimeEntryService';
import { useActiveTimer } from '../../../data/services/useActiveTimer';
import { ActiveTimerEntryPopover } from './ActiveTimerEntryPopover';

import { useMediaSizes } from '../../common/Styles/useMediaSizes';
import Logo, { CasePortalLogo } from '../../common/appLogo';
import { useAccess } from '../../../auth/useAccess';
import { useRecentCasesService } from '../../../data/services/useRecentCasesService';
import { useLogin } from '../../../auth/useLogin';
import { NotificationsPopover } from './NotificationsPopover';
import { appTokens } from '../../common/Styles/appTokens';
import { useIsAuthenticated } from '../../../redux/reducers/authReducer';
import { CaseControl } from '../../controls/CaseControl';
import { useCaseService } from '../../../data/services';
import { useCaseState } from '../../../redux/reducers/caseReducer';
import { useClientPortalState } from '../../../redux/reducers/clientPortalReducer';
import { useUserState } from '../../../redux/reducers/userReducer';

const useStyles = makeStyles({
    menu: {
        height: appTokens.menuHeight,
        backgroundColor: appTokens.menu.backgroundColor,
        width: '100%',
        boxShadow: `0 2px 8px 0 ${appTokens.menu.shadow}`,
        transition: `backgroundColor ${tokens.durationUltraSlow} ease`,
    },
    menuMobile: {
        backgroundColor: appTokens.verticalMenu.background,
    },
    toolbarBtn: {
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    toolbarBtnOnDark: {
        color: tokens.colorNeutralForegroundStaticInverted,
    },
    toolbar: {
        paddingInlineEnd: '.5rem',
    },
});

export interface MenuProps {
    isSideMenuOpen?: boolean;
    className?: string;
    featuresDisabled?: boolean;
}
export const MainMenu = (props: MenuProps) => {
    const { className, isSideMenuOpen = true, featuresDisabled = false } = props;

    //Hooks
    const isAuthenticated = useIsAuthenticated();
    const { logout } = useLogin();
    const themeService = useThemeService();
    const mediaSizes = useMediaSizes();
    const navigate = useNavigate();
    const caseState = useCaseState();
    const portalState = useClientPortalState();
    const { authenticatedUser } = useUserState();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const menuClasses = mergeClasses(
        commonStyles.utility.flexRow,
        styles.menu,
        mediaSizes.isUnderLaptop && styles.menuMobile,
        className,
    );
    const toolbarBtnClasses = mergeClasses(styles.toolbarBtn, mediaSizes.isUnderLaptop && styles.toolbarBtnOnDark);

    //Click Events
    const clickTheme = (e: any) => {
        e.stopPropagation();

        themeService.flipTheme();
    };

    const BriefcaseIcon = bundleIcon(Briefcase20Filled, Briefcase20Regular);

    const portalId = portalState.portals[0]?.portalId;
    if (!portalId) return null;
    const portalPath = Paths.index(portalId);

    return (
        <>
            <div className={menuClasses}>
                <Row noGap noWrap justifyContent='space-between'>
                    <Column width='auto' vAlign='middle'>
                        {mediaSizes.isUnderLaptop && (
                            <Row noGap noWrap padding='pl-2'>
                                <Column width='auto'>
                                    {!featuresDisabled && (
                                        <Menu>
                                            <MenuTrigger disableButtonEnhancement>
                                                <Button
                                                    icon={
                                                        <NavigationFilled
                                                            color={tokens.colorNeutralForegroundStaticInverted}
                                                        />
                                                    }
                                                    appearance='transparent'
                                                    data-testid='menu-toggle-button'
                                                />
                                            </MenuTrigger>

                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem
                                                        icon={<BriefcaseIcon />}
                                                        onClick={(e: any) => {
                                                            navigate(portalPath);
                                                            e.stopPropagation();
                                                        }}
                                                        data-testid='menu-item-dashboard'
                                                    >
                                                        Case Info
                                                    </MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    )}
                                </Column>
                                <Column width='auto' vAlign='middle' padding='px-2 pb-1'>
                                    {/* <Logo white /> */}
                                    <CasePortalLogo white />
                                </Column>
                            </Row>
                        )}
                        {!mediaSizes.isUnderLaptop && !isSideMenuOpen && (
                            <Row noGap noWrap padding='pl-2'>
                                <Column width='auto' vAlign='middle' padding='px-2 pb-2'>
                                    <CasePortalLogo white={themeService.isDarkTheme} />
                                </Column>
                            </Row>
                        )}
                    </Column>

                    {!!caseState.case && (
                        <Column vAlign='middle' padding='pl-3'>
                            <CaseControl caseObj={caseState.case!} size='medium' />
                        </Column>
                    )}

                    <Column width='auto' vAlign='middle'>
                        <Toolbar className={styles.toolbar}>
                            {authenticatedUser && (
                                <Persona
                                    name={authenticatedUser.name || authenticatedUser.username || authenticatedUser.email}
                                    size='small' textAlignment='center'
                                    textPosition={mediaSizes.isMobile ? 'before' : 'after'}
                                />
                            )}
                            {mediaSizes.isMobile ? (
                                <>
                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement>
                                            <Button
                                                icon={
                                                    <MoreVerticalFilled
                                                        color={tokens.colorNeutralForegroundStaticInverted}
                                                    />
                                                }
                                                appearance='outline'
                                                data-testid='menu-more-button'
                                            />
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList
                                                style={{
                                                    paddingInlineStart: '.5rem',
                                                    paddingInlineEnd: '1rem',
                                                    paddingBlockEnd: '.75rem',
                                                    alignItems: 'left',
                                                }}
                                            >
                                                <MenuItem
                                                    style={{ paddingInlineStart: '.75rem' }}
                                                    icon={
                                                        themeService.isDarkTheme ? (
                                                            <WeatherSunnyRegular />
                                                        ) : (
                                                            <WeatherMoonRegular />
                                                        )
                                                    }
                                                    onClick={clickTheme}
                                                    data-testid='menu-theme-button'
                                                >
                                                    {themeService.isDarkTheme
                                                        ? 'Switch to Light Mode'
                                                        : 'Switch to Dark Mode'}
                                                </MenuItem>
                                                <MenuItem
                                                    style={{ paddingInlineStart: '.75rem' }}
                                                    icon={<SignOut24Regular />}
                                                    onClick={(e: any) => {
                                                        e.stopPropagation();
                                                        logout();
                                                    }}
                                                    data-testid='menu-logout-button'
                                                >
                                                    Logout
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                </>
                            ) : (
                                <>
                                    

                                    <Tooltip
                                        content={themeService.isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                                        relationship='description'
                                    >
                                        <ToolbarButton
                                            className={toolbarBtnClasses}
                                            icon={
                                                themeService.isDarkTheme ? <WeatherSunnyRegular /> : <WeatherMoonRegular />
                                            }
                                            appearance='transparent'
                                            onClick={clickTheme}
                                            data-testid='menu-theme-button'
                                        />
                                    </Tooltip>

                                    {isAuthenticated && (
                                        <Tooltip content='Logout' relationship='description'>
                                            <ToolbarButton
                                                className={toolbarBtnClasses}
                                                icon={<SignOut24Regular />}
                                                appearance='transparent'
                                                onClick={(e: any) => {
                                                    logout();
                                                    e.stopPropagation();
                                                }}
                                                data-testid='menu-logout-button'
                                            />
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </Toolbar>
                    </Column>
                </Row>
            </div>
        </>
    );
};

type ActiveTimeEntryMenuControlProps = {
    activeTimeEntry?: TimeTrackingEntry;
};
const ActiveTimeEntryMenuControl = (props: ActiveTimeEntryMenuControlProps) => {
    const { activeTimeEntry } = props;

    //Hooks
    const { elapsedHours } = useActiveTimer();
    const mediaSizes = useMediaSizes();

    //Styles
    const commonStyles = useCommonStyles();

    return (
        <>
            {!!activeTimeEntry && (
                <ActiveTimerEntryPopover>
                    <div>
                        {!mediaSizes.isTablet ? (
                            <ToolbarButton
                                className={commonStyles.button.root}
                                icon={<Spinner size='extra-tiny' />}
                                appearance='subtle'
                                data-testid='menu-active-time-entry-button'
                            >
                                <Text weight='semibold'>{elapsedHours.toFixed(2)} hrs</Text>
                            </ToolbarButton>
                        ) : (
                            <ToolbarButton
                                className={commonStyles.button.root}
                                icon={<Spinner size='extra-tiny' />}
                                appearance='subtle'
                                data-testid='menu-active-time-entry-button'
                            />
                        )}
                    </div>
                </ActiveTimerEntryPopover>
            )}
        </>
    );
};
